import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Trash2, Info, X, Plus, ArrowLeftRight, Link2, GripVertical } from 'lucide-react';
import { ExerciseBlock, ExerciseTarget } from '@/types/workout';
import { TuneChip } from '@/components/ui/tune-chip';
import { WorkoutParameterRow } from '@/components/ui/workout-parameter-row';
import { RpeSelect } from '@/components/ui/rpe-select';
import { cn } from '@/lib/utils';

// Type for drag handle props from dnd-kit
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DragHandleProps = {
  attributes?: any;
  listeners?: any;
};

// Add field placeholder component
function AddFieldPlaceholder({ 
  label, 
  onClick 
}: { 
  label: string; 
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full h-10 rounded-xl transition-all cursor-pointer",
        "flex items-center justify-center gap-1.5",
        "bg-[var(--surface)] hover:bg-[var(--surface-strong)] active:scale-[0.98]",
        "text-neutral-400 hover:text-neutral-500",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400/50"
      )}
    >
      <Plus className="w-4 h-4" />
      <span className="text-sm font-medium">Add {label}</span>
    </button>
  );
}

interface ExerciseListItemProps {
  exercise: ExerciseBlock;
  index: number;
  onUpdate: (updates: Partial<ExerciseBlock>) => void;
  onSwap: () => void;
  onToggleSuperset: (groupId?: string) => void;
  isSuperset: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onDelete?: () => void;
  supersetGroupId?: string;
  supersetGroupIndex?: number;
  availableSupersetGroups?: Array<{ id: string; letter: string; exerciseCount: number }>;
  workoutId?: string;
  // Drag handle for within-group reordering (only provided when in a superset)
  dragHandleProps?: DragHandleProps;
  isDragging?: boolean;
  // Visual grouping indicators
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}

export function ExerciseListItem({
  exercise,
  index,
  onUpdate,
  onSwap,
  onToggleSuperset,
  isSuperset,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
  onDelete,
  supersetGroupId,
  supersetGroupIndex,
  availableSupersetGroups = [],
  workoutId,
  dragHandleProps,
  isDragging,
  isFirstInGroup,
  isLastInGroup,
}: ExerciseListItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSupersetSelector, setShowSupersetSelector] = useState(false);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showReorderHint, setShowReorderHint] = useState(false);
  const [showSupersetHint, setShowSupersetHint] = useState(false);
  
  // Optional field visibility state (show control if value exists)
  const [showWeightControl, setShowWeightControl] = useState(false);
  const [showRpeControl, setShowRpeControl] = useState(false);
  
  const { target } = exercise;
  
  // One-time hint keys
  const REORDER_HINT_KEY = 'kwaime-superset-reorder-hint-seen';
  const SUPERSET_HINT_KEY = 'kwaime-superset-hint-seen';
  
  // Show superset explanation on first exercise expand (any exercise)
  useEffect(() => {
    if (isExpanded) {
      const hintSeen = localStorage.getItem(SUPERSET_HINT_KEY);
      if (!hintSeen) {
        setShowSupersetHint(true);
        // Auto-dismiss after 8 seconds (longer for detailed content)
        const timer = setTimeout(() => {
          setShowSupersetHint(false);
          localStorage.setItem(SUPERSET_HINT_KEY, 'true');
        }, 8000);
        return () => clearTimeout(timer);
      }
    }
  }, [isExpanded]);
  
  const dismissSupersetHint = () => {
    setShowSupersetHint(false);
    localStorage.setItem(SUPERSET_HINT_KEY, 'true');
  };
  
  // Show hint when a superset exercise is first expanded (for reordering)
  useEffect(() => {
    if (isExpanded && isSuperset && dragHandleProps) {
      const hintSeen = localStorage.getItem(REORDER_HINT_KEY);
      if (!hintSeen) {
        setShowReorderHint(true);
        // Auto-dismiss after 6 seconds
        const timer = setTimeout(() => {
          setShowReorderHint(false);
          localStorage.setItem(REORDER_HINT_KEY, 'true');
        }, 6000);
        return () => clearTimeout(timer);
      }
    }
  }, [isExpanded, isSuperset, dragHandleProps]);
  
  const dismissReorderHint = () => {
    setShowReorderHint(false);
    localStorage.setItem(REORDER_HINT_KEY, 'true');
  };

  // Initialize optional field visibility based on existing values
  useEffect(() => {
    setShowWeightControl(target.weight != null);
    setShowRpeControl(target.rpe != null);
  }, [exercise.id]); // Reset when exercise changes

  // Determine superset group letter (A, B, C, etc.)
  const getSupersetGroupLetter = () => {
    if (!supersetGroupId) return 'A';
    if (supersetGroupIndex !== undefined) {
      return String.fromCharCode(65 + supersetGroupIndex);
    }
    return supersetGroupId.slice(-1).toUpperCase();
  };

  const supersetGroupLetter = isSuperset ? getSupersetGroupLetter() : null;

  // Format summary text for collapsed state
  const formatSummary = () => {
    const sets = target.sets || 1;
    const rest = exercise.rest_seconds || 0;
    
    if (target.mode === 'time') {
      const timeSeconds = target.duration_seconds || 0;
      return `${sets}×${timeSeconds}sec · Rest ${rest}sec`;
    } else {
      const repsValue = target.reps || 0;
      return `${sets}×${repsValue} reps · Rest ${rest}sec`;
    }
  };

  // Get the current work unit based on mode
  const workUnit = target.mode === 'time' ? 'sec' : 'reps';
  
  // Get the current work value based on unit
  const workValue = workUnit === 'sec' ? (target.duration_seconds ?? null) : (target.reps ?? null);

  // Handle work unit change (switching between reps and seconds)
  const handleWorkUnitChange = (newUnit: string) => {
    const newMode = newUnit === 'reps' ? 'reps' : 'time';
    const updates: Partial<ExerciseTarget> = { mode: newMode };
    
    // Set default value if switching to a mode with no existing value
    if (newMode === 'reps' && !target.reps) {
      updates.reps = 5;  // Default reps
    } else if (newMode === 'time' && !target.duration_seconds) {
      updates.duration_seconds = 30;  // Default duration
    }
    
    onUpdate({ target: { ...target, ...updates } });
  };

  // Handle work value change
  const handleWorkValueChange = (value: number | null) => {
    if (target.mode === 'reps') {
      onUpdate({ target: { ...target, reps: value ?? undefined } });
    } else {
      onUpdate({ target: { ...target, duration_seconds: value ?? undefined } });
    }
  };

  // Handle sets update
  const handleSetsUpdate = (value: number) => {
    onUpdate({ target: { ...target, sets: Math.max(1, value) } });
  };

  // Handle rest update
  const handleRestUpdate = (value: number) => {
    onUpdate({ rest_seconds: Math.max(0, value) });
  };

  // Prevent click propagation for action buttons
  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Superset grouping visual: left border with special corners for first/last items
  const supersetGroupStyles = isSuperset ? cn(
    "border-l-[3px] border-l-indigo-300",
    // Round top-left corner only for first item in group
    isFirstInGroup && "rounded-tl-xl",
    // Round bottom-left corner only for last item in group
    isLastInGroup && "rounded-bl-xl",
    // Remove border radius on left side for middle items
    !isFirstInGroup && "rounded-tl-none",
    !isLastInGroup && "rounded-bl-none",
  ) : "";

  return (
    <div className={cn(
      "border border-neutral-200 rounded-xl bg-white transition-all",
      isDragging && "opacity-50 scale-[1.02] shadow-lg",
      isSuperset && "bg-indigo-50/30", // Subtle background tint for grouped items
      supersetGroupStyles
    )}>
      {/* Collapsed State: Summary Card */}
      {!isExpanded && (
        <div 
          className="p-3.5 flex items-center gap-3 cursor-pointer hover:bg-neutral-50/50 transition-colors"
          onClick={() => setIsExpanded(true)}
        >
          {/* Drag handle for superset items */}
          {isSuperset && dragHandleProps && (
            <button
              {...dragHandleProps.attributes}
              {...dragHandleProps.listeners}
              className="touch-none p-1 -ml-1 text-neutral-300 hover:text-neutral-400 cursor-grab active:cursor-grabbing rounded transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="Reorder within group"
              title="Drag to reorder within superset"
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h4 className="text-sm font-semibold text-neutral-900 truncate">{exercise.name}</h4>
              {exercise.instruction && (
                <button
                  onClick={(e) => {
                    handleActionClick(e);
                    setShowDetailsPopup(true);
                  }}
                  className="p-0.5 text-neutral-300 hover:text-neutral-500 hover:bg-neutral-100 rounded transition-colors flex-shrink-0"
                  title="View exercise details"
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
              )}
              {isSuperset && supersetGroupLetter && (
                <span className="text-[10px] font-semibold text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded flex-shrink-0">
                  {supersetGroupLetter}
                </span>
              )}
            </div>
            <p className="text-xs text-neutral-500">{formatSummary()}</p>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <ChevronDown className="w-4 h-4 text-neutral-300" />
          </div>
        </div>
      )}

      {/* Expanded State */}
      {isExpanded && (
        <>
          <div className="p-3">
            {/* Header with name and actions */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {/* Drag handle for superset items */}
                {isSuperset && dragHandleProps && (
                  <button
                    {...dragHandleProps.attributes}
                    {...dragHandleProps.listeners}
                    className="touch-none p-1 -ml-1 text-neutral-300 hover:text-neutral-400 cursor-grab active:cursor-grabbing rounded transition-colors"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Reorder within group"
                    title="Drag to reorder within superset"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                )}
                <h4 className="text-base font-semibold text-neutral-900 truncate">{exercise.name}</h4>
                {exercise.instruction && (
                  <button
                    onClick={(e) => {
                      handleActionClick(e);
                      setShowDetailsPopup(true);
                    }}
                    className="p-0.5 text-neutral-300 hover:text-neutral-500 hover:bg-neutral-100 rounded transition-colors flex-shrink-0"
                    title="View exercise details"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                )}
                {isSuperset && supersetGroupLetter && (
                  <span className="text-[10px] font-semibold text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded flex-shrink-0">
                    {supersetGroupLetter}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {onDelete && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="p-1.5 text-neutral-300 hover:text-neutral-500 hover:bg-neutral-100 rounded-lg transition-colors"
                    title="Delete exercise"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    handleActionClick(e);
                    setIsExpanded(false);
                  }}
                  className="p-1.5 text-neutral-300 hover:text-neutral-500 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* One-time superset explanation (shows first, takes priority) */}
            {showSupersetHint && !showReorderHint && (
              <div className="mb-3 p-2.5 bg-amber-50 border border-amber-100 rounded-lg animate-in fade-in-0 slide-in-from-top-1 duration-200">
                <div className="flex items-start gap-2">
                  <div className="flex-1 text-xs text-amber-700">
                    <span className="font-medium">Tip: Supersets</span>
                    <p className="mt-1">
                      Link exercises together to create a superset — alternate between them with minimal rest for intensity.
                    </p>
                    <p className="mt-1 text-amber-600">
                      Tap <span className="font-medium">Link</span> to add an exercise to a superset group.
                    </p>
                  </div>
                  <button
                    onClick={dismissSupersetHint}
                    className="p-0.5 text-amber-400 hover:text-amber-600 transition-colors"
                    aria-label="Dismiss hint"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* One-time hint for superset reordering (shows after superset hint dismissed) */}
            {showReorderHint && !showSupersetHint && (
              <div className="mb-3 p-2.5 bg-indigo-50 border border-indigo-100 rounded-lg animate-in fade-in-0 slide-in-from-top-1 duration-200">
                <div className="flex items-start gap-2">
                  <div className="flex-1 text-xs text-indigo-700">
                    <span className="font-medium">Tip:</span> Drag the handle to reorder within the superset. Use <span className="font-medium">Group up/down</span> buttons to move the entire superset.
                  </div>
                  <button
                    onClick={dismissReorderHint}
                    className="p-0.5 text-indigo-400 hover:text-indigo-600 transition-colors"
                    aria-label="Dismiss hint"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Two-column layout: Parameters (70%) | Utilities - stacks on mobile */}
            <div className="flex flex-col sm:grid sm:grid-cols-[70%_1fr] sm:gap-4" onClick={handleActionClick}>
              {/* Left: Parameters */}
              <div className="space-y-2">
                {/* Core parameters */}
                <WorkoutParameterRow
                  label="Sets"
                  control={
                    <TuneChip
                      value={target.sets || 1}
                      unit=""
                      onChange={(value) => handleSetsUpdate(value ?? 1)}
                      step={1}
                      min={1}
                    />
                  }
                />
                
                <WorkoutParameterRow
                  label="Work"
                  control={
                    <TuneChip
                      value={workValue}
                      unit={workUnit}
                      units={['reps', 'sec']}
                      onUnitChange={handleWorkUnitChange}
                      onChange={handleWorkValueChange}
                      step={workUnit === 'reps' ? 1 : 5}
                      min={1}
                    />
                  }
                />
                
                <WorkoutParameterRow
                  label="Rest"
                  control={
                    <TuneChip
                      value={exercise.rest_seconds || 0}
                      unit="sec"
                      onChange={(value) => handleRestUpdate(value ?? 0)}
                      step={5}
                      min={0}
                    />
                  }
                />
                
                {/* Secondary parameters - optional fields with add placeholders */}
                <div className="mt-4 pt-3 border-t border-neutral-100 space-y-2">
                  <WorkoutParameterRow
                    label="Weight"
                    isSecondary
                    control={
                      showWeightControl ? (
                        <TuneChip
                          value={target.weight ?? null}
                          unit="kg"
                          onChange={(value) => {
                            onUpdate({ target: { ...target, weight: value ?? undefined } });
                            // Hide control when cleared
                            if (value === null) {
                              setShowWeightControl(false);
                            }
                          }}
                          step={2.5}
                          min={0}
                          allowNull
                          variant="secondary"
                        />
                      ) : (
                        <AddFieldPlaceholder 
                          label="weight" 
                          onClick={() => {
                            setShowWeightControl(true);
                            // Set a default value when adding
                            if (target.weight == null) {
                              onUpdate({ target: { ...target, weight: 20 } });
                            }
                          }} 
                        />
                      )
                    }
                  />
                  
                  <WorkoutParameterRow
                    label="RPE"
                    isSecondary
                    control={
                      showRpeControl ? (
                        <RpeSelect
                          value={target.rpe ?? null}
                          onChange={(value) => {
                            onUpdate({ target: { ...target, rpe: value ?? undefined } });
                            // Hide control when cleared
                            if (value === null) {
                              setShowRpeControl(false);
                            }
                          }}
                          autoOpen={target.rpe == null}
                        />
                      ) : (
                        <AddFieldPlaceholder 
                          label="RPE" 
                          onClick={() => {
                            setShowRpeControl(true);
                          }} 
                        />
                      )
                    }
                  />
                </div>
              </div>

              {/* Utilities - bottom bar on mobile, right column on desktop */}
              <div className="flex flex-row justify-center gap-1 pt-3 mt-3 border-t border-neutral-100
                              sm:flex-col sm:justify-start sm:items-start sm:gap-1 sm:pt-1 sm:mt-0 sm:border-t-0">
                
                {/* Superset */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isSuperset) {
                        onToggleSuperset();
                      } else {
                        setShowSupersetSelector(!showSupersetSelector);
                      }
                    }}
                    className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 
                               min-w-[44px] min-h-[44px] sm:min-w-[40px] sm:min-h-[40px] px-2 py-2
                               text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100/60 
                               active:scale-[0.96] rounded-lg transition-all duration-100 cursor-pointer"
                  >
                    <Link2 className="w-5 h-5 sm:w-[18px] sm:h-[18px]" />
                    <span className="text-[10px] sm:text-xs">{isSuperset ? 'Unlink' : 'Link'}</span>
                  </button>
                  
                  {showSupersetSelector && !isSuperset && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-md p-2 min-w-[160px] z-10">
                      <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wide mb-2 px-2">
                        Select Group
                      </div>
                      {availableSupersetGroups.length > 0 && (
                        <div className="space-y-0.5 mb-2">
                          {availableSupersetGroups.map((group) => (
                            <button
                              key={group.id}
                              onClick={() => {
                                onToggleSuperset(group.id);
                                setShowSupersetSelector(false);
                              }}
                              className="w-full text-left px-2 py-1.5 text-xs hover:bg-neutral-50 rounded transition-colors"
                            >
                              Group {group.letter} ({group.exerciseCount})
                            </button>
                          ))}
                        </div>
                      )}
                      <button
                        onClick={() => {
                          onToggleSuperset();
                          setShowSupersetSelector(false);
                        }}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-neutral-50 rounded transition-colors border-t border-neutral-100 mt-1 pt-1.5 font-medium"
                      >
                        + New Group
                      </button>
                    </div>
                  )}
                </div>

                {/* Swap */}
                <button
                  onClick={(e) => { e.stopPropagation(); onSwap(); }}
                  className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 
                             min-w-[44px] min-h-[44px] sm:min-w-[40px] sm:min-h-[40px] px-2 py-2
                             text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100/60 
                             active:scale-[0.96] rounded-lg transition-all duration-100 cursor-pointer"
                >
                  <ArrowLeftRight className="w-5 h-5 sm:w-[18px] sm:h-[18px]" />
                  <span className="text-[10px] sm:text-xs">Swap</span>
                </button>

                {/* Move Up */}
                {onMoveUp && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                    disabled={!canMoveUp}
                    className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 
                               min-w-[44px] min-h-[44px] sm:min-w-[40px] sm:min-h-[40px] px-2 py-2
                               text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100/60 
                               active:scale-[0.96] rounded-lg transition-all duration-100 cursor-pointer
                               disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:active:scale-100"
                    title={isSuperset ? "Moves the entire superset" : undefined}
                  >
                    <ChevronUp className="w-5 h-5 sm:w-[18px] sm:h-[18px]" />
                    <span className="text-[10px] sm:text-xs">{isSuperset ? 'Group' : 'Up'}</span>
                  </button>
                )}

                {/* Move Down */}
                {onMoveDown && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                    disabled={!canMoveDown}
                    className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 
                               min-w-[44px] min-h-[44px] sm:min-w-[40px] sm:min-h-[40px] px-2 py-2
                               text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100/60 
                               active:scale-[0.96] rounded-lg transition-all duration-100 cursor-pointer
                               disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:active:scale-100"
                    title={isSuperset ? "Moves the entire superset" : undefined}
                  >
                    <ChevronDown className="w-5 h-5 sm:w-[18px] sm:h-[18px]" />
                    <span className="text-[10px] sm:text-xs">{isSuperset ? 'Group' : 'Down'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Click outside to close selector */}
      {showSupersetSelector && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowSupersetSelector(false)}
        />
      )}

      {/* Exercise Details Modal - polished */}
      {showDetailsPopup && (
        <>
          <div
            className="fixed inset-0 bg-black/10 backdrop-blur-[1px] z-50"
            onClick={() => setShowDetailsPopup(false)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none p-4">
            <div
              className="bg-white rounded-xl shadow-lg max-w-sm w-full p-5 pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="text-base font-semibold text-neutral-900">{exercise.name}</h3>
                <button
                  onClick={() => setShowDetailsPopup(false)}
                  className="p-1 text-neutral-300 hover:text-neutral-500 hover:bg-neutral-100 rounded-lg transition-colors flex-shrink-0 -mr-1 -mt-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {exercise.instruction && (
                <p className="text-sm text-neutral-600 leading-relaxed">{exercise.instruction}</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
